import { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import Radio from "@mui/material/Radio";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHubspot } from "@fortawesome/free-brands-svg-icons";
import {
  getHubSpotContact,
  searchHubSpotContacts,
} from "../services/hubspotService";
import {
  buildHubSpotContactUrl,
  parseHubSpotContactReference,
  HUBSPOT_ORANGE,
} from "../utils/hubspot";
import type { HubSpotContactSuggestion } from "@event-planner/shared";

/**
 * The match dialog — the organizer's control point for linking (ADR-172
 * review-first model). Opens from any gray sprocket; NOTHING is written
 * from here. On open it searches HubSpot by the person's email AND name
 * in parallel (a contact filed under a different email still surfaces
 * by name), shows the candidates, and lets the organizer refine the
 * search, paste a record URL, or walk away. Confirming hands the picked
 * contact to the page, which routes the pulled data into the person's
 * edit form for review — persistence happens only at form Save.
 */

export interface HubSpotMatchTarget {
  personId: string;
  email: string;
  displayName: string;
}

interface HubSpotMatchDialogProps {
  /** null = closed. */
  target: HubSpotMatchTarget | null;
  onClose: () => void;
  /** Organizer picked a contact — the page applies or navigates. */
  onConfirm: (suggestion: HubSpotContactSuggestion) => void;
}

function mergeDedupe(
  ...lists: HubSpotContactSuggestion[][]
): HubSpotContactSuggestion[] {
  const seen = new Set<string>();
  const merged: HubSpotContactSuggestion[] = [];
  for (const list of lists) {
    for (const suggestion of list) {
      if (!seen.has(suggestion.contactId)) {
        seen.add(suggestion.contactId);
        merged.push(suggestion);
      }
    }
  }
  return merged;
}

export function HubSpotMatchDialog({
  target,
  onClose,
  onConfirm,
}: HubSpotMatchDialogProps) {
  const [queryText, setQueryText] = useState("");
  /** Results of the on-open email+name search (shown when input is empty). */
  const [autoResults, setAutoResults] = useState<HubSpotContactSuggestion[]>([]);
  /** Results of a manual refinement search / URL paste. */
  const [manualResults, setManualResults] = useState<
    HubSpotContactSuggestion[] | null
  >(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [autoSearchDone, setAutoSearchDone] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Out-of-order guard for debounced manual searches AND the on-open
  // auto search (a stale response must never land after a reset).
  const generationRef = useRef(0);

  // On open: reset, then search by email and name in parallel.
  useEffect(() => {
    if (!target) return;
    const generation = ++generationRef.current;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setQueryText("");
    setAutoResults([]);
    setManualResults(null);
    setSelectedId(null);
    setErrorText(null);
    setAutoSearchDone(false);
    setLoading(true);

    const email = target.email.trim().toLowerCase();
    const name = target.displayName.trim();
    const queries: string[] = [];
    if (email.length >= 3) queries.push(email);
    if (name.length >= 3 && name.toLowerCase() !== email) queries.push(name);

    Promise.allSettled(
      queries.map((q) => searchHubSpotContacts({ query: q })),
    ).then((settled) => {
      if (generationRef.current !== generation) return;
      const lists = settled
        .filter(
          (s): s is PromiseFulfilledResult<{ success: true; results: HubSpotContactSuggestion[] }> =>
            s.status === "fulfilled",
        )
        .map((s) => s.value.results);
      if (lists.length === 0) {
        const firstError = settled.find(
          (s): s is PromiseRejectedResult => s.status === "rejected",
        );
        setErrorText(
          firstError?.reason instanceof Error
            ? firstError.reason.message
            : "HubSpot search failed.",
        );
      }
      // Email results first — the exact-match track is the strongest signal.
      setAutoResults(mergeDedupe(...lists));
      setAutoSearchDone(true);
      setLoading(false);
    });
  }, [target]);

  useEffect(
    () => () => {
      generationRef.current += 1;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  function handleInputChange(next: string) {
    const generation = ++generationRef.current;
    setQueryText(next);
    setErrorText(null);
    setSelectedId(null);
    // Manual interaction supersedes the on-open search. If that request is
    // invalidated before it settles, manual empty/error results must still be
    // able to render the direct-search fallback.
    setAutoSearchDone(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = next.trim();
    if (!trimmed) {
      setManualResults(null);
      setLoading(false);
      return;
    }

    const parsed = parseHubSpotContactReference(trimmed);
    if (parsed.error) {
      setManualResults([]);
      setLoading(false);
      setErrorText(parsed.error);
      return;
    }
    const pastedId = parsed.contactId;
    if (!pastedId && trimmed.length < 3) {
      // Too short to search — and the cleared timer above may have left
      // a pending search's spinner behind.
      setLoading(false);
      return;
    }

    setLoading(true);
    debounceRef.current = setTimeout(() => {
      const request = pastedId
        ? getHubSpotContact({ contactId: pastedId }).then((r) => [r.contact])
        : searchHubSpotContacts({ query: trimmed }).then((r) => r.results);
      request
        .then((results) => {
          if (generationRef.current !== generation) return;
          setManualResults(results);
          // A pasted URL resolves to exactly one contact — preselect it.
          if (pastedId && results.length === 1) {
            setSelectedId(results[0].contactId);
          }
        })
        .catch((err) => {
          if (generationRef.current !== generation) return;
          setManualResults([]);
          setErrorText(
            err instanceof Error ? err.message : "HubSpot search failed.",
          );
        })
        .finally(() => {
          if (generationRef.current === generation) setLoading(false);
        });
    }, 300);
  }

  const results = manualResults ?? autoResults;
  const selected = results.find((r) => r.contactId === selectedId) ?? null;
  const showFallback = !loading && autoSearchDone && results.length === 0;
  const externalSearchUrl = target
    ? buildHubSpotContactUrl({ email: target.email })
    : null;

  return (
    <Dialog open={!!target} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <FontAwesomeIcon
            icon={faHubspot}
            style={{ fontSize: 18, color: HUBSPOT_ORANGE }}
          />
          <span>Link {target?.displayName} to HubSpot</span>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Potential HubSpot matches for {target?.displayName}, searched by
          email and name. Pick one and continue — their CRM details are
          loaded into the profile form for you to review, and nothing is
          saved until you save the profile.
        </DialogContentText>

        <TextField
          fullWidth
          label="Refine search"
          placeholder="Name, email, or a pasted contact record URL"
          value={queryText}
          onChange={(e) => handleInputChange(e.target.value)}
          slotProps={{
            input: {
              endAdornment: loading ? <CircularProgress size={18} /> : null,
            },
            htmlInput: { "data-testid": "hubspot-match-input" },
          }}
          sx={{ mb: 1.5 }}
        />

        {errorText && (
          <Typography color="error" variant="body2" sx={{ mb: 1 }}>
            {errorText}
          </Typography>
        )}

        <List dense disablePadding data-testid="hubspot-match-results">
          {results.map((suggestion) => {
            const name =
              [suggestion.firstName, suggestion.lastName]
                .filter(Boolean)
                .join(" ") ||
              suggestion.email ||
              "Unknown";
            const secondary = [suggestion.email, suggestion.company]
              .filter(Boolean)
              .join(" · ");
            const isSelected = selectedId === suggestion.contactId;
            return (
              <ListItemButton
                key={suggestion.contactId}
                selected={isSelected}
                onClick={() => setSelectedId(suggestion.contactId)}
                data-testid={`hubspot-match-option-${suggestion.contactId}`}
                sx={{
                  borderRadius: 1,
                  border: 1,
                  borderColor: isSelected ? "primary.main" : "divider",
                  mb: 1,
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontWeight: 500 }}>{name}</Typography>
                  {secondary && (
                    <Typography variant="body2" color="text.secondary">
                      {secondary}
                    </Typography>
                  )}
                  {suggestion.jobTitle && (
                    <Typography variant="caption" color="text.secondary">
                      {suggestion.jobTitle}
                    </Typography>
                  )}
                </Box>
                {/* Selection indicator only — the row button is the
                    interactive element, so the radio is inert and
                    hidden from the accessibility tree. */}
                <Radio
                  edge="end"
                  checked={isSelected}
                  disableRipple
                  slotProps={{
                    input: { "aria-hidden": true, tabIndex: -1 },
                  }}
                  sx={{ ml: 1, pointerEvents: "none" }}
                />
              </ListItemButton>
            );
          })}
        </List>

        {showFallback && (
          <Box
            sx={{ py: 1 }}
            data-testid={
              errorText
                ? "hubspot-match-error-fallback"
                : "hubspot-match-no-results"
            }
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {errorText
                ? "HubSpot search is unavailable. You can still look for this contact directly:"
                : `No HubSpot contacts match ${target?.displayName}${
                    target?.email ? ` or ${target.email}` : ""
                  }. Try a different search above, paste the contact's record URL, or look in HubSpot directly:`}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              href={externalSearchUrl ?? ""}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hubspot-match-open-search"
            >
              Search in HubSpot
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!selected}
          onClick={() => selected && onConfirm(selected)}
          data-testid="hubspot-match-confirm"
        >
          Link & review
        </Button>
      </DialogActions>
    </Dialog>
  );
}
